import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Image as ImageIcon, FileText, Map, Trash2, ChevronDown, Loader2, Search, ShieldAlert } from 'lucide-react';
import { useStore, getActivePage } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { imageFileToDataUri, pdfFileToDataUri, osmToDataUri, geocode, ensureLeaflet } from '@/lib/locationBg';

export const LocationBackgroundControl = () => {
  const { t } = useI18n();
  const page = useStore(getActivePage);
  const setLocationBackground = useStore((s) => s.setLocationBackground);
  const imgRef = useRef(null);
  const pdfRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [osmOpen, setOsmOpen] = useState(false);
  const [osmConsent, setOsmConsent] = useState(false);
  const [osm, setOsm] = useState({ lat: '52.5200', lng: '13.4050', zoom: '16', query: '' });
  const [osmBusy, setOsmBusy] = useState(false);
  const mapRef = useRef(null);
  const mapElRef = useRef(null);

  // Spin up an interactive Leaflet preview while the OSM dialog is open. Panning
  // / zooming the map keeps the lat/lng/zoom fields in sync (moveend). The map is
  // torn down when the dialog closes.
  useEffect(() => {
    if (!osmOpen || !osmConsent) return undefined;
    let cancelled = false;
    let onMove = null;
    ensureLeaflet()
      .then((L) => {
        if (cancelled || !mapElRef.current || mapRef.current) return;
        const lat0 = parseFloat(osm.lat);
        const lng0 = parseFloat(osm.lng);
        const z0 = parseInt(osm.zoom, 10);
        const map = L.map(mapElRef.current, { zoomControl: true, attributionControl: true });
        map.setView([Number.isFinite(lat0) ? lat0 : 52.52, Number.isFinite(lng0) ? lng0 : 13.405], Number.isFinite(z0) ? z0 : 16);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '\u00a9 OpenStreetMap' }).addTo(map);
        mapRef.current = map;
        onMove = () => {
          const ctr = map.getCenter();
          setOsm((s) => ({ ...s, lat: ctr.lat.toFixed(4), lng: ctr.lng.toFixed(4), zoom: String(map.getZoom()) }));
        };
        map.on('moveend', onMove);
        setTimeout(() => {
          try {
            map.invalidateSize();
          } catch {
            /* ignore */
          }
        }, 120);
      })
      .catch(() => toast.error(t('osm_error')));
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try {
          if (onMove) mapRef.current.off('moveend', onMove);
          mapRef.current.remove();
        } catch {
          /* ignore */
        }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osmOpen, osmConsent]);

  if (!page) return null;
  const kind = page.background?.kind || 'none';
  const kindLabel = kind === 'image' ? t('bg_image_set') : kind === 'pdf' ? t('bg_pdf_set') : kind === 'osm' ? t('bg_osm_set') : t('bg_none');

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const dataUri = await imageFileToDataUri(file);
      setLocationBackground(page.id, { kind: 'image', dataUri });
    } catch {
      toast.error(t('bg_image_error'));
    } finally {
      setBusy(false);
    }
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const dataUri = await pdfFileToDataUri(file);
      setLocationBackground(page.id, { kind: 'pdf', dataUri });
    } catch {
      toast.error(t('pdf_error'));
    } finally {
      setBusy(false);
    }
  };

  const runGeocode = async () => {
    if (!osm.query.trim()) return;
    setOsmBusy(true);
    try {
      const r = await geocode(osm.query.trim());
      if (mapRef.current) {
        mapRef.current.setView([r.lat, r.lng], mapRef.current.getZoom() || parseInt(osm.zoom, 10) || 16);
      } else {
        setOsm((s) => ({ ...s, lat: r.lat.toFixed(4), lng: r.lng.toFixed(4) }));
      }
    } catch {
      toast.error(t('osm_search_error'));
    } finally {
      setOsmBusy(false);
    }
  };

  // Apply manually typed coordinates/zoom to the preview map (Enter key).
  const applyCoords = () => {
    if (!mapRef.current) return;
    const lat = parseFloat(osm.lat);
    const lng = parseFloat(osm.lng);
    const zoom = parseInt(osm.zoom, 10);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      mapRef.current.setView([lat, lng], Number.isFinite(zoom) ? zoom : mapRef.current.getZoom());
    }
  };

  const loadOsm = async () => {
    // Prefer the live map's framed area so the saved image matches the preview.
    let lat = parseFloat(osm.lat);
    let lng = parseFloat(osm.lng);
    let zoom = parseInt(osm.zoom, 10);
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      lat = c.lat;
      lng = c.lng;
      zoom = mapRef.current.getZoom();
    }
    if (Number.isNaN(lat) || Number.isNaN(lng) || Number.isNaN(zoom)) return;
    setOsmBusy(true);
    try {
      const dataUri = await osmToDataUri({ lat, lng, zoom });
      setLocationBackground(page.id, { kind: 'osm', dataUri, meta: { lat, lng, zoom } });
      setOsmOpen(false);
    } catch {
      toast.error(t('osm_error'));
    } finally {
      setOsmBusy(false);
    }
  };

  return (
    <>
      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="location-image-input" />
      <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={onPdf} data-testid="location-pdf-input" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5 text-xs" disabled={busy} data-testid="location-bg-menu-trigger">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
            <span className="text-muted-foreground">{t('bg_background')}:</span>
            <span className="font-semibold">{busy ? t('bg_loading') : kindLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => imgRef.current?.click()} data-testid="location-bg-image">
            <ImageIcon className="mr-2 h-4 w-4" /> {t('bg_image')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => pdfRef.current?.click()} data-testid="location-bg-pdf">
            <FileText className="mr-2 h-4 w-4" /> {t('bg_pdf')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { setOsmConsent(false); setOsmOpen(true); }} data-testid="location-bg-osm">
            <Map className="mr-2 h-4 w-4" /> {t('bg_osm')}
          </DropdownMenuItem>
          {kind !== 'none' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setLocationBackground(page.id, { kind: 'none', dataUri: '' })}
                className="text-[#B42318] focus:text-[#B42318]"
                data-testid="location-bg-remove"
              >
                <Trash2 className="mr-2 h-4 w-4" /> {t('bg_remove')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={osmOpen} onOpenChange={(o) => { setOsmOpen(o); if (!o) setOsmConsent(false); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="osm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Map className="h-4 w-4" /> {t('osm_title')}
            </DialogTitle>
          </DialogHeader>

          {!osmConsent ? (
            <div className="py-2" data-testid="osm-consent">
              <div className="flex gap-3 rounded-lg border border-[#B7791F]/30 bg-[rgba(183,121,31,0.08)] p-4">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#B7791F]" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">{t('osm_consent_title')}</div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/80">{t('osm_consent_desc')}</p>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="ghost" onClick={() => setOsmOpen(false)} data-testid="osm-consent-cancel">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={() => setOsmConsent(true)}
                  className="bg-[#0B5FFF] hover:bg-[#0a55e6]"
                  data-testid="osm-consent-accept"
                >
                  <Map className="mr-2 h-4 w-4" /> {t('osm_consent_accept')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
          <>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('osm_search')}</Label>
                <Input
                  value={osm.query}
                  onChange={(e) => setOsm((s) => ({ ...s, query: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && runGeocode()}
                  placeholder={t('osm_search_placeholder')}
                  className="h-9"
                  data-testid="osm-search-input"
                />
              </div>
              <Button variant="outline" size="sm" className="h-9" onClick={runGeocode} disabled={osmBusy} data-testid="osm-search-button">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Interactive preview: pan/zoom to frame the exact area. */}
            <div
              ref={mapElRef}
              className="h-[320px] w-full overflow-hidden rounded-md border border-border bg-[#e9e9e4]"
              data-testid="osm-preview-map"
            />

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('osm_lat')}</Label>
                <Input value={osm.lat} onChange={(e) => setOsm((s) => ({ ...s, lat: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && applyCoords()} className="h-9 font-mono text-sm" data-testid="osm-lat-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('osm_lng')}</Label>
                <Input value={osm.lng} onChange={(e) => setOsm((s) => ({ ...s, lng: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && applyCoords()} className="h-9 font-mono text-sm" data-testid="osm-lng-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('osm_zoom')}</Label>
                <Input value={osm.zoom} onChange={(e) => setOsm((s) => ({ ...s, zoom: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && applyCoords()} className="h-9 font-mono text-sm" data-testid="osm-zoom-input" />
              </div>
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('osm_preview_hint')}</p>
          </div>
          <DialogFooter>
            <Button onClick={loadOsm} disabled={osmBusy} className="bg-[#0B5FFF] hover:bg-[#0a55e6]" data-testid="osm-load-button">
              {osmBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Map className="mr-2 h-4 w-4" />}
              {t('osm_load')}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
